import { useMemo, useState } from 'react';
import { TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import PageLayout from '../../components/Page/PageLayout';
import EmptyState from '../../components/Page/EmptyState';
import CustomerSuggestionCard from '../../components/Migration/CustomerSuggestionCard';
import RemoveCustomerSuggestionModal from './RemoveCustomerSuggestionModal';
import ReviewCustomerSuggestionModal from './ReviewCustomerSuggestionModal';
import ConnectCustomerSuggestionModal from './ConnectCustomerSuggestionModal';
import AddCustomerFromSuggestionModal from './AddCustomerFromSuggestionModal';
import {
  needsConnect,
  hasBrokenLink,
  LinkedCustomerSuggestion,
} from './customerSuggestions.helpers';
import { useMigrationCandidateCustomerSuggestions } from '../../hooks/queries/migration.queries';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';

function CustomerSuggestions() {
  const { t } = useTranslation('common', { keyPrefix: 'customerSuggestions' });
  const [searchValue, setSearchValue] = useState('');
  const [removeTarget, setRemoveTarget] = useState<MigrationCandidateBusinessPartnerBody | null>(
    null
  );
  const [reviewTarget, setReviewTarget] = useState<MigrationCandidateBusinessPartnerBody | null>(
    null
  );
  const [connectTarget, setConnectTarget] = useState<LinkedCustomerSuggestion | null>(null);
  const [addCustomerTarget, setAddCustomerTarget] =
    useState<MigrationCandidateBusinessPartnerBody | null>(null);

  const { data, isLoading } = useMigrationCandidateCustomerSuggestions();

  const filteredItems = useMemo(() => {
    // Rows claiming to be linked with no linked Business Partner at all are a
    // should-never-happen shape: neither Review nor Connect can do anything
    // useful with them, so they never reach rendering or search filtering.
    const items = (data?.items ?? []).filter((item) => !hasBrokenLink(item));
    if (!searchValue.trim()) return items;
    const query = searchValue.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [data?.items, searchValue]);

  const itemsCount = filteredItems.length;
  const showPlaceholder = isLoading || itemsCount === 0;

  const emptyText = searchValue.trim() ? t('noSearchResults') : t('noCustomers');

  /**
   * Review is one button with a state-driven destination: a suggestion that
   * already needs Connect (an Exact Match, or a previously-Merged suggestion
   * still awaiting its Service Agreement) has no choice left to offer, so it
   * skips match selection and goes straight to the Connect wizard.
   */
  const handleReview = (suggestion: MigrationCandidateBusinessPartnerBody) => {
    if (needsConnect(suggestion)) {
      setConnectTarget(suggestion);
      return;
    }
    setReviewTarget(suggestion);
  };

  return (
    <PageLayout
      title={t('title')}
      itemCount={itemsCount}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchPlaceholder={t('searchPlaceholder')}
    >
      {showPlaceholder ? (
        <EmptyState
          loading={isLoading}
          icon="handshake"
          text={isLoading ? t('loading') : emptyText}
          subtext={searchValue.trim() ? undefined : t('noCustomersDescription')}
        />
      ) : (
        <TeContainerSlim column fill gap="s" overflow="auto">
          {filteredItems.map((suggestion) => (
            <CustomerSuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              mode={needsConnect(suggestion) ? 'connect' : 'review'}
              onReview={handleReview}
              onRemove={setRemoveTarget}
            />
          ))}
        </TeContainerSlim>
      )}
      <RemoveCustomerSuggestionModal
        suggestion={removeTarget}
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
      />
      <ReviewCustomerSuggestionModal
        suggestion={reviewTarget}
        open={reviewTarget !== null}
        onClose={() => setReviewTarget(null)}
        onAddAsNewCustomer={(suggestion) => {
          setReviewTarget(null);
          setAddCustomerTarget(suggestion);
        }}
        onConnect={(merged) => {
          setReviewTarget(null);
          setConnectTarget(merged);
        }}
      />
      {/*
       * Mounted only while a suggestion is being connected, so the wizard's
       * step, agreement type and customer number start clean for each one
       * (`useState` seeds on mount only — the same fix the identity-side
       * add-organization modal needed).
       */}
      {connectTarget && (
        <ConnectCustomerSuggestionModal
          suggestion={connectTarget}
          open
          onClose={() => setConnectTarget(null)}
        />
      )}
      {addCustomerTarget && (
        <AddCustomerFromSuggestionModal
          suggestion={addCustomerTarget}
          open
          onClose={() => setAddCustomerTarget(null)}
        />
      )}
    </PageLayout>
  );
}

export default CustomerSuggestions;
